import React, { useState, useRef, useEffect } from 'react';
import { ZoomIn, ZoomOut, RotateCcw, Edit, Minus, Circle, Type, X, Save, CheckCircle } from 'lucide-react';

const EditPopup = ({ selectedImage, onSave, onCancel }) => {
  const [zoomLevel, setZoomLevel] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [drawingMode, setDrawingMode] = useState('freehand');
  const [drawingColor, setDrawingColor] = useState('#ff0000');
  const [lineWidth, setLineWidth] = useState(3);
  const [textInput, setTextInput] = useState('');
  const [isAddingText, setIsAddingText] = useState(false);
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showThicknessPicker, setShowThicknessPicker] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  const canvasRef = useRef(null);
  const imageRef = useRef(null);
  const isDrawing = useRef(false);
  const lastX = useRef(0);
  const lastY = useRef(0);
  const textElements = useRef([]);
  const shapes = useRef([]);

  // Initialize canvas when component mounts
  useEffect(() => {
    if (selectedImage && (selectedImage.type === 'JPEG' || selectedImage.type === 'DICOM') && selectedImage.imageUrl) {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      const ctx = canvas.getContext('2d');
      
      const img = new Image();
      img.onload = () => {
        // Set canvas size to match image but limit to 90% of viewport (increased from 70%)
        const maxWidth = window.innerWidth * 0.9;
        const maxHeight = window.innerHeight * 0.8;
        
        let width = img.width;
        let height = img.height;
        
        if (width > maxWidth) {
          const ratio = maxWidth / width;
          width = maxWidth;
          height = height * ratio;
        }
        
        if (height > maxHeight) {
          const ratio = maxHeight / height;
          height = maxHeight;
          width = width * ratio;
        }
        
        canvas.width = width;
        canvas.height = height;
        
        ctx.drawImage(img, 0, 0, width, height);
        
        imageRef.current = img;
        
        redraw();
      };
      img.src = selectedImage.imageUrl;
    }
  }, [selectedImage]);

  // Show success notification with improved animation
  const showSuccessNotification = () => {
    setShowSuccess(true);
    setTimeout(() => {
      setShowSuccess(false);
    }, 5000);
  };

  // Redraw function
  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, canvas.width, canvas.height);
    }
    
    // Draw shapes with null checks
    shapes.current.forEach(shape => {
      if (!shape || !shape.points || shape.points.length === 0) return;
      
      ctx.beginPath();
      ctx.strokeStyle = shape.color || '#ff0000';
      ctx.lineWidth = shape.lineWidth || 3;
      
      if (shape.type === 'freehand' || shape.type === 'line') {
        ctx.moveTo(shape.points[0].x, shape.points[0].y);
        for (let i = 1; i < shape.points.length; i++) {
          if (shape.points[i]) {
            ctx.lineTo(shape.points[i].x, shape.points[i].y);
          }
        }
        ctx.stroke();
      } else if (shape.type === 'circle') {
        if (shape.points.length >= 2 && shape.points[0] && shape.points[1]) {
          const radius = Math.sqrt(
            Math.pow(shape.points[1].x - shape.points[0].x, 2) + 
            Math.pow(shape.points[1].y - shape.points[0].y, 2)
          );
          ctx.arc(shape.points[0].x, shape.points[0].y, radius, 0, 2 * Math.PI);
          ctx.stroke();
        }
      }
    });
    
    // Draw text elements with null checks
    textElements.current.forEach(text => {
      if (!text) return;
      ctx.font = '16px Arial';
      ctx.fillStyle = text.color || '#ff0000';
      ctx.fillText(text.text || '', text.x || 0, text.y || 0);
    });
  };

  // Handle mouse events for drawing
  const handleMouseDown = (e) => {
    if (!(selectedImage.type === 'JPEG' || selectedImage.type === 'DICOM') || !selectedImage.imageUrl) return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    
    isDrawing.current = true;
    lastX.current = x;
    lastY.current = y;
    
    if (drawingMode === 'text') {
      setIsAddingText(true);
      setTextPosition({ x, y });
    } else {
      shapes.current.push({
        type: drawingMode,
        points: [{ x, y }],
        color: drawingColor,
        lineWidth: lineWidth
      });
    }
  };

  const handleMouseMove = (e) => {
    if (!isDrawing.current || drawingMode === 'text') return;
    
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX - rect.left) / zoomLevel;
    const y = (e.clientY - rect.top) / zoomLevel;
    
    if (shapes.current.length === 0) return;
    
    const currentShape = shapes.current[shapes.current.length - 1];
    if (!currentShape) return;
    
    if (drawingMode === 'freehand') {
      currentShape.points.push({ x, y });
      redraw();
    } else if (drawingMode === 'line' || drawingMode === 'circle') {
      if (currentShape.points.length === 1) {
        currentShape.points.push({ x, y });
      } else {
        currentShape.points[1] = { x, y };
      }
      redraw();
    }
    
    lastX.current = x;
    lastY.current = y;
  };

  const handleMouseUp = () => {
    isDrawing.current = false;
  };

  const handleAddText = () => {
    if (textInput.trim() !== '') {
      textElements.current.push({
        text: textInput,
        x: textPosition.x,
        y: textPosition.y,
        color: drawingColor
      });
      setTextInput('');
      setIsAddingText(false);
      redraw();
    }
  };

  const handleSave = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const dataURL = canvas.toDataURL('image/png');
    
    console.log('Saved annotated image:', dataURL);
    
    // Show the success notification immediately
    showSuccessNotification();
    
    // Call onSave after a short delay to allow the notification to appear
    setTimeout(() => {
      onSave();
    }, 500);
  };

  const handleClearAnnotations = () => {
    shapes.current = [];
    textElements.current = [];
    redraw();
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 0.1, 3));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 0.1, 0.5));
  };

  const handleRotate = () => {
    setRotation(prev => (prev + 90) % 360);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      {/* Beautiful Success Notification with Animation */}
      <div className={`fixed top-4 right-4 z-[60] transition-all duration-300 transform ${
        showSuccess 
          ? 'translate-x-0 opacity-100 scale-100' 
          : 'translate-x-full opacity-0 scale-95'
      }`}>
        <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-6 py-4 rounded-lg shadow-2xl flex items-center min-w-[280px]">
          <div className="flex items-center">
            <div className="bg-white bg-opacity-20 rounded-full p-1 mr-3">
              <CheckCircle className="w-6 h-6" />
            </div>
            <div>
              <div className="font-semibold text-lg">Success!</div>
              <div className="text-sm text-green-100">Image saved successfully</div>
            </div>
          </div>
          <div className="ml-4 w-1 h-12 bg-white bg-opacity-30 rounded-full overflow-hidden">
            <div 
              className="w-full bg-white bg-opacity-60 rounded-full transition-all duration-3000 ease-linear"
              style={{
                height: showSuccess ? '0%' : '100%',
                transition: showSuccess ? 'height 3000ms linear' : 'none'
              }}
            ></div>
          </div>
        </div>
      </div>
      
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl h-full max-h-[95vh] flex flex-col">
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-xl font-semibold">
            Editing: {selectedImage.name}
          </h3>
          <button
            onClick={onCancel}
            className="p-2 text-gray-500 hover:text-gray-700"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="flex-1 overflow-hidden flex flex-col p-4 bg-gray-100">
          <div className="flex-1 overflow-auto flex justify-center items-center mb-4">
            <div 
              className="bg-white p-2 shadow-md inline-block relative"
              style={{ 
                transform: `scale(${zoomLevel}) rotate(${rotation}deg)`,
                transition: 'transform 0.3s ease'
              }}
            >
              {(selectedImage.type === 'JPEG' || selectedImage.type === 'DICOM') && selectedImage.imageUrl ? (
                <>
                  <canvas
                    ref={canvasRef}
                    onMouseDown={handleMouseDown}
                    onMouseMove={handleMouseMove}
                    onMouseUp={handleMouseUp}
                    onMouseLeave={handleMouseUp}
                    className="max-w-full max-h-[70vh] object-contain cursor-crosshair"
                  />
                  {isAddingText && (
                    <div 
                      className="absolute p-2 bg-white border border-gray-300 rounded shadow-md"
                      style={{ left: `${textPosition.x}px`, top: `${textPosition.y}px` }}
                    >
                      <input
                        type="text"
                        value={textInput}
                        onChange={(e) => setTextInput(e.target.value)}
                        placeholder="Enter text..."
                        className="border border-gray-300 p-1 mr-2"
                        autoFocus
                      />
                      <button 
                        onClick={handleAddText}
                        className="bg-blue-500 text-white px-2 py-1 rounded mr-2"
                      >
                        Add
                      </button>
                      <button 
                        onClick={() => setIsAddingText(false)}
                        className="bg-gray-500 text-white px-2 py-1 rounded"
                      >
                        Cancel
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="w-96 h-64 flex items-center justify-center bg-gray-200">
                  <div className="text-center">
                    <div className="text-xl font-bold">{selectedImage.type} File</div>
                    <div className="text-gray-600">Preview not available</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="bg-white p-3 rounded-lg shadow">
            <div className="flex flex-wrap justify-center items-center gap-2">
              {/* Zoom Controls */}
              <div className="flex items-center gap-1">
                <button 
                  onClick={handleZoomIn}
                  className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-xs"
                >
                  <ZoomIn className="w-4 h-4 mr-1" />
                  Zoom In
                </button>
                <button 
                  onClick={handleZoomOut}
                  className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-xs"
                >
                  <ZoomOut className="w-4 h-4 mr-1" />
                  Zoom Out
                </button>
              </div>
              
              <button 
                onClick={handleRotate}
                className="px-2 py-1 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center text-xs"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Rotate
              </button>
              
              {/* Drawing Tools */}
              <div className="flex items-center gap-1 border-l border-gray-300 pl-2">
                <button 
                  onClick={() => setDrawingMode('freehand')}
                  className={`px-2 py-1 rounded flex items-center text-xs ${
                    drawingMode === 'freehand' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Edit className="w-4 h-4 mr-1" />
                  Freehand
                </button>
                <button 
                  onClick={() => setDrawingMode('line')}
                  className={`px-2 py-1 rounded flex items-center text-xs ${
                    drawingMode === 'line' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Minus className="w-4 h-4 mr-1" />
                  Line
                </button>
                <button 
                  onClick={() => setDrawingMode('circle')}
                  className={`px-2 py-1 rounded flex items-center text-xs ${
                    drawingMode === 'circle' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Circle className="w-4 h-4 mr-1" />
                  Circle
                </button>
                <button 
                  onClick={() => setDrawingMode('text')}
                  className={`px-2 py-1 rounded flex items-center text-xs ${
                    drawingMode === 'text' ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  }`}
                >
                  <Type className="w-4 h-4 mr-1" />
                  Text
                </button>
              </div>
              
              {/* Color Dropdown */}
              <div className="relative border-l border-gray-300 pl-2">
                <button 
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center text-xs"
                  onClick={() => setShowColorPicker(!showColorPicker)}
                >
                  <div 
                    className="w-4 h-4 rounded-full border border-gray-300 mr-1" 
                    style={{ backgroundColor: drawingColor }}
                  ></div>
                  Color
                </button>
                {showColorPicker && (
                  <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg p-2 w-32 z-10">
                    <div className="grid grid-cols-4 gap-2">
                      {['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff', '#000000', '#ffffff'].map(color => (
                        <div
                          key={color}
                          className="w-6 h-6 rounded-full cursor-pointer border border-gray-300"
                          style={{ backgroundColor: color }}
                          onClick={() => {
                            setDrawingColor(color);
                            setShowColorPicker(false);
                          }}
                        ></div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              
              {/* Thickness Dropdown */}
              <div className="relative border-l border-gray-300 pl-2">
                <button 
                  className="px-2 py-1 rounded bg-gray-200 text-gray-700 hover:bg-gray-300 flex items-center text-xs"
                  onClick={() => setShowThicknessPicker(!showThicknessPicker)}
                >
                  <div 
                    className="w-4 h-1 bg-gray-800 rounded-full mr-1" 
                    style={{ height: `${lineWidth}px` }}
                  ></div>
                  Thickness
                </button>
                {showThicknessPicker && (
                  <div className="absolute bottom-full left-0 mb-1 bg-white border border-gray-200 rounded-md shadow-lg p-2 w-20 z-10">
                    {[1, 3, 5, 8].map(width => (
                      <div
                        key={width}
                        className="flex items-center py-1 px-2 hover:bg-gray-100 rounded cursor-pointer"
                        onClick={() => {
                          setLineWidth(width);
                          setShowThicknessPicker(false);
                        }}
                      >
                        <div 
                          className="w-6 h-1 bg-gray-800 rounded-full mr-2" 
                          style={{ height: `${width}px` }}
                        ></div>
                        <span className="text-xs">{width}px</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              <button 
                onClick={handleClearAnnotations}
                className="px-2 py-1 bg-red-500 text-white rounded hover:bg-red-600 flex items-center text-xs border-l border-gray-300 pl-2"
              >
                <X className="w-4 h-4 mr-1" />
                Clear All
              </button>

              {/* Save and Cancel buttons */}
              <div className="flex items-center gap-1 border-l border-gray-300 pl-2">
                <button 
                  onClick={handleSave}
                  className="px-2 py-1 bg-green-500 text-white rounded hover:bg-green-600 flex items-center text-xs transform transition-transform hover:scale-105"
                >
                  <Save className="w-4 h-4 mr-1" />
                  Save
                </button>
                <button 
                  onClick={onCancel}
                  className="px-2 py-1 bg-gray-500 text-white rounded hover:bg-gray-600 text-xs flex items-center"
                >
                  <X className="w-4 h-4 mr-1" />
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditPopup;